import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LOGO_URL = 'https://static.readdy.ai/image/cc883008a4d7bcfeafa6a2e5816cdde1/145343b2bde13a888d3f705e3f2c3ea4.png';
const NEWSLETTER_URL = 'https://readdy.ai/api/form/d6vfqvnb245v7h34onp0';

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-darkBlue-500">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="BientoMart" className="w-10 h-10 object-contain rounded-lg" />
              <span className="font-display text-xl font-bold text-white">BientoMart</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">{t('footer.tagline')}</p>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-facebook-fill', href: 'https://www.facebook.com/profile.php?id=61584207860930' },
                { icon: 'ri-twitter-x-fill', href: 'https://www.x.com/M97Bienvenu' },
                { icon: 'ri-instagram-line', href: 'https://www.instagram.com/bientomart/?hl=en' },
                { icon: 'ri-linkedin-fill', href: 'https://www.linkedin.com/company/112266406/admin/dashboard/' },
                { icon: 'ri-youtube-fill', href: '#' },
                { icon: 'ri-tiktok-fill', href: 'https://www.tiktok.com/@bientomart' },
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center bg-darkBlue-600 rounded-lg text-clairBlue-300 hover:bg-clairBlue-300 hover:text-white transition-all"
                >
                  <i className={social.icon}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t('footer.services')}</h3>
            <ul className="space-y-3">
              {[
                { label: t('footer.websiteCreation'), path: '/services?tab=website' },
                { label: t('footer.smm'), path: '/services?tab=smm' },
                { label: t('footer.traffic'), path: '/services?tab=traffic' },
                { label: t('footer.aiReviews'), path: '/ai-tools?tab=text' },
              ].map(item => (
                <li key={item.label}>
                  <Link to={item.path} className="text-gray-400 text-sm hover:text-clairBlue-300 transition-colors flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-clairBlue-400 text-xs"></i>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-5">{t('footer.resources')}</h3>
            <ul className="space-y-3">
              {[
                t('footer.blog'), t('footer.caseStudies'), t('footer.tutorials'), t('footer.faq'), t('footer.privacy'), t('footer.terms')
              ].map(item => (
                <li key={item}>
                  <a href="#" className="text-gray-400 text-sm hover:text-clairBlue-300 transition-colors flex items-center gap-2">
                    <i className="ri-arrow-right-s-line text-clairBlue-400 text-xs"></i>
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Column */}
          <div>
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-3">{t('footer.newsletter')}</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-5">{t('footer.newsletterDesc')}</p>
            <form
              data-readdy-form
              id="footer-newsletter-form"
              onSubmit={handleSubscribe}
              className="flex gap-2"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('footer.emailPlaceholder')}
                className="flex-1 bg-darkBlue-600 text-white text-sm placeholder-gray-500 px-4 py-2.5 rounded-lg border border-darkBlue-400 focus:outline-none focus:border-clairBlue-300 min-w-0"
                required
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="bg-clairBlue-300 hover:bg-clairBlue-400 text-darkBlue-500 font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"
              >
                {status === 'loading' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-fill"></i>}
              </button>
            </form>
            {status === 'success' && (
              <p className="mt-2 text-clairBlue-300 text-xs flex items-center gap-1">
                <i className="ri-check-circle-line"></i> Successfully subscribed!
              </p>
            )}
            {status === 'error' && (
              <p className="mt-2 text-red-400 text-xs">Something went wrong. Please try again.</p>
            )}
            <div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line text-teal-400 mt-1"></i>
                  <div className="text-gray-400 text-sm">
                    <div>+256 773 515 447</div>
                    <div>+254 111 441 487</div>
                    <div>+250 787 258 369</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-mail-line text-teal-400 mt-1"></i>
                  <span className="text-gray-400 text-sm">bientomart@gmail.com</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-teal-400 mt-1"></i>
                  <span className="text-gray-400 text-sm">
                    7H98+FPM, Entebbe Road,
                    <br />
                    Kampala, Uganda
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="border-t border-darkBlue-600 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-gray-500 text-sm">{t('footer.copyright')}</p>
        <div className="flex items-center gap-4">
          <a href="#" className="text-gray-500 hover:text-clairBlue-300 text-sm transition-colors">{t('footer.privacy')}</a>
          <span className="text-gray-600">·</span>
          <a href="#" className="text-gray-500 hover:text-clairBlue-300 text-sm transition-colors">{t('footer.terms')}</a>
        </div>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/256773515447"
        rel="nofollow"
        target="_blank"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center transition-all hover:scale-110 cursor-pointer"
        style={{ animation: 'waPulse 2s infinite' }}
      >
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>

      <style>{`
        @keyframes waPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
          50% { box-shadow: 0 0 0 12px rgba(34,197,94,0); }
        }
      `}</style>
    </footer>
  );
}
